import { useState, useRef, useEffect } from "react";
import { Link } from "react-router-dom";
import { categories } from "@/mocks/categories";

export default function Navbar() {
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [mobileCatOpen, setMobileCatOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const closeTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (mobileMenuOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => { document.body.style.overflow = ""; };
  }, [mobileMenuOpen]);

  const closeMobileMenu = () => {
    setMobileMenuOpen(false);
    setMobileCatOpen(false);
  };

  const handleMouseEnter = () => {
    if (closeTimer.current) clearTimeout(closeTimer.current);
    setDropdownOpen(true);
  };

  const handleMouseLeave = () => {
    closeTimer.current = setTimeout(() => setDropdownOpen(false), 200);
  };

  return (
    <>
      <nav className="fixed top-0 left-0 right-0 z-50 h-16 bg-background-50/95 backdrop-blur-md border-b border-background-200/70">
        <div className="w-full h-full px-4 md:px-10">
          <div className="flex items-center justify-between h-full">
            <Link to="/" className="flex items-center gap-2.5 flex-shrink-0">
              <div className="w-9 h-9 rounded-lg bg-primary-500 flex items-center justify-center">
                <i className="ri-file-list-3-line text-background-50 text-lg"></i>
              </div>
              <span className="font-heading font-bold text-foreground-950 text-sm md:text-base whitespace-nowrap">
                AllChinaBuy Sheet
              </span>
            </Link>

            <div className="hidden md:flex items-center gap-1">
              <Link
                to="/"
                className="px-4 py-2 text-foreground-700 hover:text-foreground-950 text-sm font-medium transition-colors whitespace-nowrap rounded-lg hover:bg-background-100"
              >
                Home
              </Link>

              <div
                className="relative"
                ref={dropdownRef}
                onMouseEnter={handleMouseEnter}
                onMouseLeave={handleMouseLeave}
              >
                <button className="flex items-center gap-1.5 px-4 py-2 text-foreground-700 hover:text-foreground-950 text-sm font-medium transition-colors whitespace-nowrap rounded-lg hover:bg-background-100 cursor-pointer">
                  Categories
                  {dropdownOpen ? (
                    <i className="ri-arrow-up-s-line text-base transition-transform"></i>
                  ) : (
                    <i className="ri-arrow-down-s-line text-base transition-transform"></i>
                  )}
                </button>

                {dropdownOpen && (
                  <div className="absolute top-full left-0 mt-1 bg-background-50 border border-background-200/70 rounded-xl py-2 min-w-[240px] max-h-[460px] overflow-y-auto grid grid-cols-1">
                    {categories.map((cat) => (
                      <a
                        key={cat.name}
                        href={cat.url}
                        target="_blank"
                        rel="noopener nofollow"
                        className="flex items-center gap-3 px-4 py-2.5 text-sm text-foreground-700 hover:bg-primary-50 hover:text-primary-700 transition-colors whitespace-nowrap"
                      >
                        <span className="w-7 h-7 rounded-md bg-background-100 flex items-center justify-center flex-shrink-0">
                          <i className="ri-price-tag-3-line text-foreground-500 text-xs"></i>
                        </span>
                        {cat.displayName}
                      </a>
                    ))}
                  </div>
                )}
              </div>

              <Link
                to="/allchinabuy-spreadsheet-guide"
                className="px-4 py-2 text-foreground-700 hover:text-foreground-950 text-sm font-medium transition-colors whitespace-nowrap rounded-lg hover:bg-background-100"
              >
                Shopping Guide
              </Link>

              <a
                href="https://kaspreadsheet.com"
                target="_blank"
                rel="noopener nofollow"
                className="inline-flex items-center gap-2 ml-3 px-5 py-2.5 bg-primary-500 text-background-50 dark:text-foreground-950 text-sm font-semibold rounded-full hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
              >
                <i className="ri-shopping-cart-2-line text-base"></i>
                Buy It Now
              </a>
            </div>

            <button
              className="md:hidden w-10 h-10 flex items-center justify-center rounded-lg hover:bg-background-100 transition-colors cursor-pointer"
              onClick={() => setMobileMenuOpen(true)}
              aria-label="Open menu"
            >
              <i className="ri-menu-line text-foreground-950 text-2xl"></i>
            </button>
          </div>
        </div>
      </nav>

      {mobileMenuOpen && (
        <>
          <div
            className="fixed inset-0 bg-black/40 z-40 md:hidden"
            onClick={closeMobileMenu}
          />

          <div className="fixed top-0 right-0 bottom-0 w-[85vw] max-w-[380px] z-50 bg-background-50 overflow-y-auto md:hidden">
            <div className="flex items-center justify-between px-5 py-4 border-b border-background-200/70">
              <span className="text-sm font-semibold text-foreground-700 font-heading">Menu</span>
              <button
                className="w-10 h-10 flex items-center justify-center rounded-lg hover:bg-background-100 transition-colors cursor-pointer"
                onClick={closeMobileMenu}
                aria-label="Close menu"
              >
                <i className="ri-close-line text-foreground-950 text-2xl"></i>
              </button>
            </div>

            <div className="px-4 py-4 space-y-1">
              <Link
                to="/"
                className="flex items-center gap-3 px-4 py-3.5 rounded-xl text-foreground-700 hover:bg-background-100 hover:text-foreground-950 text-sm font-medium transition-colors"
                onClick={closeMobileMenu}
              >
                <span className="w-9 h-9 rounded-lg bg-primary-100 flex items-center justify-center">
                  <i className="ri-home-4-line text-primary-600 text-lg"></i>
                </span>
                Home
              </Link>

              <button
                className="w-full flex items-center justify-between gap-3 px-4 py-3.5 rounded-xl text-foreground-700 hover:bg-background-100 hover:text-foreground-950 text-sm font-medium transition-colors cursor-pointer"
                onClick={() => setMobileCatOpen(!mobileCatOpen)}
              >
                <span className="flex items-center gap-3">
                  <span className="w-9 h-9 rounded-lg bg-accent-100 flex items-center justify-center">
                    <i className="ri-grid-line text-accent-600 text-lg"></i>
                  </span>
                  Categories
                </span>
                <span className={`transition-transform duration-200 ${mobileCatOpen ? "rotate-180" : ""}`}>
                  <i className="ri-arrow-down-s-line text-foreground-500 text-lg"></i>
                </span>
              </button>

              <div
                className={`overflow-hidden transition-all duration-300 ease-in-out ${
                  mobileCatOpen ? "max-h-[600px] opacity-100" : "max-h-0 opacity-0"
                }`}
              >
                <div className="ml-4 pl-4 border-l-2 border-background-200/70 space-y-0.5 py-1">
                  {categories.map((cat) => (
                    <a
                      key={cat.name}
                      href={cat.url}
                      target="_blank"
                      rel="noopener nofollow"
                      className="flex items-center gap-2.5 px-3 py-2.5 rounded-lg text-sm text-foreground-600 hover:text-primary-700 hover:bg-primary-50 transition-colors"
                      onClick={closeMobileMenu}
                    >
                      <span className="w-2 h-2 rounded-full bg-primary-300 flex-shrink-0"></span>
                      {cat.displayName}
                    </a>
                  ))}
                </div>
              </div>

              <Link
                to="/allchinabuy-spreadsheet-guide"
                className="flex items-center gap-3 px-4 py-3.5 rounded-xl text-foreground-700 hover:bg-background-100 hover:text-foreground-950 text-sm font-medium transition-colors"
                onClick={closeMobileMenu}
              >
                <span className="w-9 h-9 rounded-lg bg-secondary-100 flex items-center justify-center">
                  <i className="ri-book-open-line text-secondary-600 text-lg"></i>
                </span>
                Shopping Guide
              </Link>

              <div className="pt-4 mt-3 border-t border-background-200/70">
                <a
                  href="https://kaspreadsheet.com"
                  target="_blank"
                  rel="noopener nofollow"
                  className="flex items-center justify-center gap-2 w-full px-5 py-3.5 bg-primary-500 text-background-50 dark:text-foreground-950 text-sm font-semibold rounded-full hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
                  onClick={closeMobileMenu}
                >
                  <i className="ri-shopping-cart-2-line text-base"></i>
                  Buy It Now
                </a>
              </div>
            </div>
          </div>
        </>
      )}
    </>
  );
}